import React, { useEffect, useState } from 'react';
import { ErrorMessage, Formik } from 'formik';
import Swal from "sweetalert2";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrums from '../../../common/breadcrumbs';
import CustomCkeditor from '../../../common/customeditor';
import * as emailTemplateService from '../../../services/email.template';
import { SWAL_SETTINGS } from '../../../../../utils/Constants';
import { globalLoader, handleServerValidations } from '../../../../../utils/commonfunction';
import * as Yup from 'yup';
import { CheckPicker, CustomProvider, Tooltip, Whisper } from 'rsuite';
import { copyText } from '../../../../../utils/commonfunction';
import { useTranslation } from 'react-i18next';
import { getTemplateCategories } from './list';

const CATEGORY_OPTIONS = [
    { label: "All", value: "all" },
    { label: "Basic User", value: "basic_user" },
    { label: "Parent", value: "parent" },
    { label: "Player", value: "player" },
    { label: "Club Admin", value: "clubadmin" },
    { label: "Team Manager", value: "team_manager" },
    { label: "Admin", value: "admin" },
];

const EmailTempEdit = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const [showdefault, setShowDefault] = useState({});
    const [dataLoaded, setDataLoaded] = useState(false);
    const [saveType, setSaveType] = useState('')
    const { t } = useTranslation()
    const breadcrumbs = [{ title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("automatic_emails"), url: "/admin/cms/default-email-template/list/1" },
    { title: t("btn_edit"), url: "" }]

    const queryParams = new URLSearchParams(location.search);
    const devParam = queryParams.get('dev');
    const isDev = devParam === 'true' || devParam === '1';
    const [isDevMode, setIsDevMode] = useState(isDev);

    useEffect(() => {
        setIsDevMode(isDev);
    }, [isDev]);

    const [defaultLayout, setDefaultLayout] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const layoutResponse = await emailTemplateService.Layout();
                if (layoutResponse && layoutResponse.success) {
                    setDefaultLayout(layoutResponse.data.layout);
                }
                const detailsResponse = await emailTemplateService.Details(params.id);
                setShowDefault(detailsResponse && detailsResponse.data ? detailsResponse.data : []);
                setDataLoaded(true);
            } catch (error) {
                console.log("error=====>", error);
                setDataLoaded(true);
            }
        };
        fetchData();
    }, []);


    const validationSchema = Yup.object().shape({
        title: Yup.string().required(t("label_title_error")).max(90, t("max_length_label")).test('no-spaces', t("label_title_error"), (value) => value.trim()),
        slug: Yup.string().required(t("label_slug_error")).test('no-spaces', t("label_slug_error"), (value) => value.trim()),
        subject: Yup.string().required(t("label_subject_error")).max(90, t("max_length_label")).test('no-spaces', t("label_subject_error"), (value) => value.trim()),
        richtext: Yup.string().required(t("label_richtext_error")).test('no-spaces', t("label_richtext_error"), (value) => value.trim()),
    });

    const handleClick = (text) => {
        copyText(text);
        Swal.fire({
            icon: 'success',
            text: 'Copied',
            ...SWAL_SETTINGS,
        })
    }

    const addOption = (currentValue, setFieldValue) => {
        setFieldValue("options", [...currentValue, ""]);
    };
    const removeOption = (currentValue, index, setFieldValue) => {
        let options = [...currentValue];
        options.splice(index, 1);
        setFieldValue("options", options);
    };
    const unescapeHTML = (str) => {
        if (!str) return "";
        return str
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&amp;/g, "&")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");
    };

    const stripLayout = (html) => {
        if (!html) return "";

        /** 1. Try to find content markers first (most accurate) */
        const match = html.match(/<!-- CONTENT_START -->([\s\S]*?)<!-- CONTENT_END -->/);
        if (match) return match[1].trim();

        /** 2. If it contains a full HTML document, use the layout stripping logic */
        if (html.includes('<!DOCTYPE html>') || html.includes('<html')) {
            // Look for the master layout container (td with specific padding/color)
            const bodyMatch = html.match(/<td[^>]*style="[^"]*padding:\s*20px;[^"]*color:\s*#333333;[^"]*"[^>]*>([\s\S]*?)<\/td>/i);
            if (bodyMatch) return bodyMatch[1].trim();

            // Final fallback: body content
            const genericBodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
            if (genericBodyMatch) return genericBodyMatch[1].trim();
        }

        /** 
         * 3. Fragment Handling:
         * If the content starts with <tr>, check if it's the master layout's wrapper.
         * If it's just a fragment of multiple rows (like the user's case with &nbsp; row), 
         * we should NOT strip it unless it matches the master layout's specific container row.
         */
        if (html.trim().toLowerCase().startsWith("<tr")) {
            // Only strip if it contains the master layout style
            const masterRowMatch = html.match(/<td[^>]*style="[^"]*padding:\s*20px;[^"]*color:\s*#333333;[^"]*"[^>]*>([\s\S]*?)<\/td>/i);
            if (masterRowMatch) return masterRowMatch[1].trim();

            // If it doesn't match the master style, it might be a user-defined table structure, keep it.
        }

        return html.trim();
    };

    const ensureButtonStyles = (html) => {
        if (!html) return "";
        const defaultBtnStyle = 'style="background-color: #007d7f; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; font-family: Arial, sans-serif; font-size: 14px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"';
        return html.replace(/<a\s+(?![^>]*style=)([^>]*>(?:View Invoice|View Invoice in Stripe|View Summary)[\s\S]*?<\/a>)/gi, `<a ${defaultBtnStyle} $1`);
    };

    const getInitialRichText = () => {
        if (!showdefault || !showdefault.description) return "";

        let content = showdefault.description;

        // If it looks escaped, unescape it BEFORE stripping
        if (content.includes('&lt;!DOCTYPE') || content.includes('&lt;html') || content.includes('&lt;tr')) {
            content = unescapeHTML(content);
        }

        // Strip layout
        content = stripLayout(content);

        // Ensure button CSS is attached to View Invoice links
        content = ensureButtonStyles(content);

        // Always wrap in current defaultLayout for the editor preview
        if (defaultLayout) {
            content = defaultLayout.replace('{{MESSAGE_BODY}}', content);
        }

        return content;
    };

    const mapCategoryToValue = (cat) => {
        if (!cat) return "";
        const lower = String(cat).toLowerCase().trim();
        if (lower === "clubadmin" || lower === "club admin") return "clubadmin";
        if (lower === "basic_user" || lower === "basicuser" || lower === "basic user") return "basic_user";
        if (lower === "team_manager" || lower === "team manager") return "team_manager";
        if (lower === "parent") return "parent";
        if (lower === "player") return "player";
        if (lower === "admin") return "admin";
        if (lower === "all") return "all";
        return lower;
    };

    const getInitialCategory = (data) => {
        if (!data) return [];
        let raw = [];
        if (data.category) {
            if (Array.isArray(data.category)) raw = data.category;
            else if (typeof data.category === 'string') raw = data.category.split(',').map(s => s.trim());
        } else {
            raw = getTemplateCategories(data);
        }
        return Array.from(new Set(raw.map(mapCategoryToValue).filter(Boolean)));
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <Formik
                enableReinitialize
                initialValues={{
                    title: showdefault && showdefault.title ? showdefault.title : "",
                    title_de: showdefault && showdefault.title_de ? showdefault.title_de : "",
                    subject: showdefault && showdefault.subject ? showdefault.subject : "",
                    richtext: getInitialRichText(),
                    options: showdefault && showdefault.options && showdefault.options?.length ? showdefault.options : [""],
                    slug: showdefault && showdefault.slug ? showdefault.slug : "",
                    category: getInitialCategory(showdefault),
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting, resetForm }) => {
                    globalLoader(true);
                    const id = params.id;
                    let formData = new FormData();
                    formData.append('title', values.title);
                    formData.append('subject', values.subject);
                    formData.append('description', stripLayout(values.richtext));
                    formData.append('category', JSON.stringify(values.category || []));

                    formData.append('slug', values.slug);
                    if (values.options) {
                        formData.append('options', JSON.stringify(values.options));
                    }
                    emailTemplateService.Edit(formData, id).then(response => {
                        setSubmitting(false);
                        if (response.success) {
                            Swal.fire({
                                icon: 'success',
                                text: response.message,
                                ...SWAL_SETTINGS
                            })
                            if (saveType !== 'Save') {
                                setTimeout(() => {
                                    navigate(`/admin/cms/default-email-template/list/${params.pgno}`)
                                }, 1000);
                            }
                            globalLoader(false);
                        } else {
                            Swal.fire({
                                icon: 'error',
                                text: handleServerValidations(response),
                                ...SWAL_SETTINGS
                            });
                            globalLoader(false);
                        }
                    }).catch(error => {
                        console.log("error ====> ", error);
                    })
                }}
            >{({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                setFieldTouched,
                isSubmitting,

            }) => (
                <form onSubmit={handleSubmit}>
                    <div className="row row-sm">
                        <div className="col-lg-12 col-md-12 animation_fade">
                            <div className="card custom-card">
                                <div className="card-body">
                                    <div>
                                        <h6 className="main-content-label mb-3">{t("edit_automatic_email")}</h6>
                                    </div>
                                    <div className="row row-sm">
                                        <div className='col-md-6 text-center form-group'>
                                            <label htmlFor='title' className='text-left d-flex'>{t("title")}:<span className="requirestar">*</span> </label>
                                            <input
                                                name='title'
                                                id='title'
                                                type='text'
                                                placeholder='Enter the website title'
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.title}
                                                className='form-control'
                                            />
                                            <span className='text-danger d-flex text-left'><ErrorMessage name={"title"} /></span>
                                        </div>
                                        <div className='col-md-6 text-center form-group'>
                                            <label htmlFor='slug' className='text-left d-flex'>{t("label_slug")}:<span className="requirestar">*</span> </label>
                                            <input
                                                disabled name='slug'
                                                type='text'
                                                id="slug"
                                                placeholder='Enter the slug'
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.slug}
                                                className='form-control'
                                            />
                                            <span className='text-danger d-flex text-left'><ErrorMessage name={"slug"} /></span>
                                        </div>
                                        <div className='col-md-6 text-center form-group'>
                                            <label htmlFor='subject' className='text-left d-flex'>{t("list_heading_subject")}:<span className="requirestar">*</span> </label>
                                            <input
                                                name='subject'
                                                type='text'
                                                id="subject"
                                                placeholder='Enter the subject'
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.subject}
                                                className='form-control'
                                            />
                                            <span className='text-danger d-flex text-left'><ErrorMessage name={"subject"} /></span>
                                        </div>
                                        <div className='col-md-6 text-center form-group'>
                                            <label htmlFor='category' className='text-left d-flex'>Category:</label>
                                            <CustomProvider>
                                                <CheckPicker
                                                    data={CATEGORY_OPTIONS}
                                                    value={values.category || []}
                                                    onChange={(val) => setFieldValue('category', val)}
                                                    placeholder="Select Categories"
                                                    style={{ width: "100%" }}
                                                    cleanable={true}
                                                    searchable={false}
                                                />
                                            </CustomProvider>
                                        </div>
                                        {
                                            isDev && values?.options && values?.options.length ?
                                                <>
                                                    <div className='col-12 mb-2'>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <label htmlFor={"option" + (values?.options.length - 1)} className='text-left d-flex align-items-center mb-0'>{t("label_dynamic_options")}<span className="text-warning ms-2">({t("click_to_copy")})</span>
                                                                <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip>{t("short_code")}</Tooltip>}>
                                                                    <span className='field-more-info mt-1 ms-1 cp'>?</span>
                                                                </Whisper></label>
                                                            {!isDevMode ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm py-1 px-2 rounded d-flex align-items-center"
                                                                    style={{ fontSize: '12px' }}
                                                                    onClick={() => setIsDevMode(true)}
                                                                >
                                                                    <i className="ri-add-line me-1" style={{ fontSize: '14px' }}></i> Add/Edit Options
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-secondary btn-sm py-1 px-2 rounded d-flex align-items-center"
                                                                    style={{ fontSize: '12px' }}
                                                                    onClick={() => setIsDevMode(false)}
                                                                >
                                                                    Hide Edit Options
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-12 mb-2">
                                                        {values?.options && values?.options.filter((item) => item !== "").map((item, index) => (
                                                            <span onClick={() => handleClick('{' + item + '}')} key={index} className="badge badge-dark my-2 me-2 cp">{'{' + item + '}'}</span>
                                                        ))}
                                                        {isDevMode && (
                                                            <div className="d-flex flex-wrap mt-2">
                                                                {values?.options && values?.options.map((item, index) => (
                                                                    <div className="mb-3 d-flex align-items-center me-4" key={index}>
                                                                        <input name={"options[" + index + "]"} value={item} onChange={handleChange} onBlur={handleBlur} type="text" style={{ width: "150px" }} className="form-control input-sm" id={"option" + (index)} placeholder="New dynamic option" autoFocus={true} />
                                                                        {
                                                                            values?.options?.length > 1 ?
                                                                                <button className='btn btn-main-danger bg-secondary text-white btn-sm py-0 px-1 rounded-circle ms-2' type='button' onClick={() => removeOption(values?.options, index, setFieldValue)}><i className="ri-close-large-line"></i></button>
                                                                                : null
                                                                        }
                                                                        {
                                                                            index === values?.options?.length - 1 ?
                                                                                <button className='btn btn-main-primary signbtn btn-sm py-0 px-1 rounded-circle ms-2' type='button' onClick={() => addOption(values?.options, setFieldValue)}><i className="ri-add-large-line"></i></button>
                                                                                : null
                                                                        }
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                                : null
                                        }
                                        <div className='col-lg-12 text-center form-group'>
                                            <label htmlFor='subject' className='text-left d-flex'>{t("label_description")}
                                                :<span className="requirestar">*</span></label>
                                            {
                                                dataLoaded ?
                                                    <CustomCkeditor
                                                        fieldname={"richtext"}
                                                        setFieldValue={setFieldValue}
                                                        value={values.richtext}
                                                        setFieldTouched={setFieldTouched}
                                                    />
                                                    :
                                                    ""
                                            }
                                            <span className='text-danger d-flex text-left'>{errors.richtext && touched.richtext && errors.richtext}</span>
                                        </div>

                                        <div className="">
                                            <button onClick={() => { setSaveType('Save') }} className="btn btn-info mr-2" type="submit" >
                                                <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                                {t("btn_save")}
                                            </button>
                                            <button onClick={() => { setSaveType('') }} className="btn btn-success mr-2" type="submit">
                                                <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                                {t("btn_save_exit")}
                                            </button>
                                            <button className="btn ripple btn-secondary" type='button' onClick={() => navigate(-1)}>
                                                <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                                {t("btn_cancel")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            )}
            </Formik>
        </>
    )
}

export default EmailTempEdit